import { useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';

interface UseTrackerOptions {
  timeoutMs?: number;
}

interface TrackerResult {
  startTransaction: (
    name: string,
    operatorName: string,
    metadata?: Record<string, any>,
  ) => void;
  stopTransaction: (metadata?: Record<string, any>) => void;
  cancelTransaction: (reason?: string) => void;
  failTransaction: (error: Error) => void;
  isTracking: boolean;
  currentTransactionName: string | null;
}

export function useTracker(options: UseTrackerOptions = {}): TrackerResult {
  const { timeoutMs } = options;

  // Reference to the current span
  const spanRef = useRef<Sentry.Span | null>(null);
  // Reference to the current transaction name
  const transactionNameRef = useRef<string | null>(null);
  // Reference to the current timeout
  const timeoutRef = useRef<number | null>(null);

  // Clean up any existing timeout
  const clearExistingTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Start a new transaction
  const startTransaction = useCallback(
    (name: string, operatorName: string, metadata?: Record<string, any>) => {
      // First clean up any existing span
      if (spanRef.current) {
        try {
          spanRef.current.end();
        } catch (e) {
          console.warn('Failed to end previous span', e);
        }

        spanRef.current = null;
        transactionNameRef.current = null;
      }

      // Clear any existing timeout
      clearExistingTimeout();

      try {
        // Start a new span with startSpanManual providing the required callback
        Sentry.startSpanManual(
          {
            name,
            op: operatorName,
            attributes: {
              ...(metadata || {}),
            },
          },
          // This is the required callback function - it receives the span
          (span) => {
            if (span) {
              // Save the span reference
              spanRef.current = span;
              transactionNameRef.current = name;

              // Add metadata as a breadcrumb if provided
              if (metadata) {
                Sentry.addBreadcrumb({
                  category: 'transaction.start',
                  message: `${name} started`,
                  level: 'info',
                  data: metadata,
                });
              }

              // Set up auto-finish timeout if specified
              if (timeoutMs) {
                timeoutRef.current = window.setTimeout(() => {
                  if (spanRef.current) {
                    // Add a breadcrumb indicating timeout
                    Sentry.addBreadcrumb({
                      category: 'transaction.timeout',
                      message: `${name} timed out after ${timeoutMs}ms`,
                      level: 'warning',
                    });

                    // End the span
                    spanRef.current.setStatus({
                      code: 2, // ERROR
                      message: `Timeout after ${timeoutMs}ms`,
                    });
                    spanRef.current.end();

                    // Clear refs
                    spanRef.current = null;
                    transactionNameRef.current = null;
                  }
                }, timeoutMs);
              }
            } else {
              console.warn('Failed to start span for', name);
            }

            // The callback doesn't need to return anything
            return undefined;
          },
        );
      } catch (e) {
        console.error('Failed to start transaction', e);

        // Add error breadcrumb
        Sentry.addBreadcrumb({
          category: 'transaction.error',
          message: `Failed to start transaction: ${name}`,
          level: 'error',
          data: {
            error: String(e),
          },
        });
      }
    },
    [clearExistingTimeout, timeoutMs],
  );

  // The rest of the methods remain largely the same
  const stopTransaction = useCallback(
    (metadata?: Record<string, any>) => {
      if (!spanRef.current) return;

      try {
        // Clear any auto-finish timeout
        clearExistingTimeout();

        // Add completion metadata if provided
        if (metadata) {
          Sentry.addBreadcrumb({
            category: 'transaction.complete',
            message: `${transactionNameRef.current} completed`,
            level: 'info',
            data: metadata,
          });

          // Add metadata as attributes to the span
          Object.entries(metadata).forEach(([key, value]) => {
            if (spanRef.current && typeof value === 'string') {
              spanRef.current.setAttribute(key, value);
            }
          });
        }

        // Set status and end the span
        spanRef.current.setStatus({
          code: 0, // OK
          message: 'Success',
        });
        spanRef.current.end();
      } catch (e) {
        console.error('Failed to stop transaction', e);
      } finally {
        // Clear refs
        spanRef.current = null;
        transactionNameRef.current = null;
      }
    },
    [clearExistingTimeout],
  );

  // Cancel the current transaction, marking it as abandoned
  const cancelTransaction = useCallback(
    (reason?: string) => {
      if (!spanRef.current) return;

      try {
        // Clear any auto-finish timeout
        clearExistingTimeout();

        // Add cancellation information as a breadcrumb
        Sentry.addBreadcrumb({
          category: 'transaction.cancelled',
          message: `${transactionNameRef.current} cancelled`,
          level: 'info',
          data: { reason: reason || 'User cancelled' },
        });

        // Set status and end the span
        spanRef.current.setStatus({
          code: 1, // CANCELLED
          message: reason || 'User cancelled',
        });
        spanRef.current.end();
      } catch (e) {
        console.error('Failed to cancel transaction', e);
      } finally {
        // Clear refs
        spanRef.current = null;
        transactionNameRef.current = null;
      }
    },
    [clearExistingTimeout],
  );

  // Mark the current transaction as failed
  const failTransaction = useCallback(
    (error: Error) => {
      if (!spanRef.current) return;

      try {
        // Clear any auto-finish timeout
        clearExistingTimeout();

        // Add error information as a breadcrumb
        Sentry.addBreadcrumb({
          category: 'transaction.error',
          message: `${transactionNameRef.current} failed`,
          level: 'error',
          data: {
            error: error.message,
            stack: error.stack,
          },
        });

        // Set status and end the span
        spanRef.current.setStatus({
          code: 2, // ERROR
          message: error.message,
        });
        spanRef.current.end();

        // Capture the exception
        Sentry.captureException(error);
      } catch (e) {
        console.error('Failed to mark transaction as failed', e);
      } finally {
        // Clear refs
        spanRef.current = null;
        transactionNameRef.current = null;
      }
    },
    [clearExistingTimeout],
  );

  return {
    startTransaction,
    stopTransaction,
    cancelTransaction,
    failTransaction,
    isTracking: spanRef.current !== null,
    currentTransactionName: transactionNameRef.current,
  };
}
